import { Box, IconButton, Link as ChakraLink, Switch } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { RiDeleteBinLine, RiFileEditLine } from "react-icons/ri";
import { TbReportAnalytics } from "react-icons/tb";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CTable from "../../../components/Table/table";
import { openAddLiveVideo } from "../../../modules/admin-modules/models/Actions";
import { toggleActiveLiveVideoRequest, deleteLiveVideoRequest } from "../../../modules/admin-modules/liveVideos/Actions";
import { brandPalette } from "../../../constants/brandPalette";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
};

const iconBtnProps = {
  variant: "ghost",
  border: "0",
  size: "sm",
  color: C.primary,
  _hover: { bg: "rgba(91, 13, 22, 0.08)", color: C.secondary },
};

const LiveVideosTable = ({ setAdd, setSelectedData, searchTerm = "" }) => {
  const liveVideos = useSelector((state) => state.adminLiveVideos.liveVideos);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector((state) => state.adminLiveVideos.isLoading);
  const [offset, setPage] = useState(0);
  const [size, setPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  const { confirmDelete, deleteConfirmModal } = useDeleteConfirm({
    title: "Delete live session?",
  });

  const list = useMemo(() => {
    const raw = Array.isArray(liveVideos) ? liveVideos : [];
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return raw;
    return raw.filter(
      (v) =>
        v?.title?.toLowerCase().includes(q) ||
        v?.zoom_link?.toLowerCase().includes(q)
    );
  }, [liveVideos, searchTerm]);

  useEffect(() => {
    setPageNumber(0);
  }, [searchTerm]);

  const viewData = (row) => {
    const plain = list.find((v) => v.id === row?.id) ?? row;
    setSelectedData(plain);
  };

  const Data = list.map((data) => ({
    ...data,
    id: data?.id,
    name: data?.title,
    link: (
      <ChakraLink
        href={data?.zoom_link}
        isExternal
        color="blue.400"
        fontWeight="700"
        textDecoration="underline"
        textUnderlineOffset="3px"
        _hover={{ color: "blue.300" }}
        onClick={(e) => e.stopPropagation()}
      >
        {data?.zoom_link || "—"}
      </ChakraLink>
    ),
    report: (
      <Box
        as="button"
        type="button"
        cursor="pointer"
        color={C.primary}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/dashboard/live-videos-report/${data?.id}`);
        }}
        aria-label="Open report"
      >
        <TbReportAnalytics size={22} />
      </Box>
    ),
    active: (
      <Box onClick={(e) => e.stopPropagation()}>
        <Switch
          size="md"
          colorScheme="green"
          isChecked={Number(data?.active) === 1}
          onChange={() => dispatch(toggleActiveLiveVideoRequest(data?.id))}
          aria-label="Toggle active"
        />
      </Box>
    ),
    edit: (
      <IconButton
        {...iconBtnProps}
        aria-label="Edit live session"
        fontSize="20px"
        icon={<RiFileEditLine />}
        onClick={(e) => {
          e.stopPropagation();
          const plain = list.find((v) => v.id === data?.id) ?? data;
          setSelectedData(plain);
          setAdd(false);
          dispatch(openAddLiveVideo());
        }}
      />
    ),
    delete: (
      <IconButton
        {...iconBtnProps}
        aria-label="Delete"
        fontSize="20px"
        icon={<RiDeleteBinLine color="#9b2c2c" />}
        onClick={(e) => {
          e.stopPropagation();
          confirmDelete({
            name: data?.title,
            onConfirm: () => dispatch(deleteLiveVideoRequest(data?.id)),
          });
        }}
      />
    ),
  }));

  const columns = [
    { Header: "Session", accessor: "name" },
    { Header: "Link", accessor: "link" },
    { Header: "Report", accessor: "report" },
    { Header: "Active", accessor: "active" },
    { Header: "Edit", accessor: "edit" },
    { Header: "Delete", accessor: "delete" },
  ];

  const totalPage = Math.max(1, Math.ceil(list.length / size));

  return (
    <>
    <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
      <Box
        bg="var(--surface-raised)"
        border="1px solid"
        borderColor="var(--border-subtle)"
        borderRadius="16px"
        overflow="hidden"
        boxShadow="sm"
      >
        <CTable
          selectedData={viewData}
          Columns={columns}
          Data={Data}
          path="openModel"
          Title=""
          subTitle=""
          btnTitle=""
          placeHolder=""
          noSearchBar
          noFilter
          footerBtnTitle
          filterList
          addButton
          addButtonTitle=""
          setPage={setPage}
          setPerPage={setPerPage}
          currentpage={pageNumber}
          setPageNumber={setPageNumber}
          noSecondSearchBar
          isLoading={isLoading}
          perPage={size}
          totalPage={totalPage}
          wrapperProps={{ maxH: "unset", overflow: "auto" }}
          tableProps={{ bg: "transparent", borderRadius: "0", mb: 0 }}
          theadProps={{ bg: "var(--surface-muted)", color: "var(--text-primary)" }}
        />
      </Box>
    </Box>
    {deleteConfirmModal}
    </>
  );
};

export default LiveVideosTable;
